// Newsletter sign-up popup (sections/signup-popup.liquid).
// Desktop: exit intent after a minimum time, with a fallback timer. Mobile: delay, or shortly into
// the second page view. Time counts while the page is visible and adds up across the session's page
// views. The popup never opens before the cookie decision plus a short gap:
// the modal would make Shopify's cookie banner unclickable. A trigger that fires earlier waits for it.
// A pause is stored only after the visitor actively closes the popup; after a sign-up the popup
// stays off for good. Its state goes to localStorage with preferences consent, else sessionStorage.
// Submitting goes through Shopify's hCaptcha (spam protection), which posts the form natively; the
// page reloads with ?customer_posted=true#SignupPopupForm and the popup shows the success view.

if (!customElements.get('signup-popup')) {
  const STORAGE_KEY = 'mara:signup-popup';
  // Address and trigger of the current attempt, read back after the reload
  const PENDING_KEY = 'mara:signup-popup-pending';
  // Visible time and page views of this session, for the triggers
  const SESSION_KEY = 'mara:signup-popup-session';
  const SECOND_VIEW_DELAY = 3000;
  const DAY = 24 * 60 * 60 * 1000;
  const BANNER_GAP = 2500;
  const CAPTCHA_WAIT = 5000;
  const PRIVACY_API_TIMEOUT = 5000;
  // GA4 stream of the Google & YouTube app, whose gtag runs on the page
  const GA4_ID = 'G-EW4MSPTFHN';
  const GTAG_POLL = 250;
  const GTAG_TIMEOUT = 10000;

  // Webmail start pages by address domain
  const MAILBOXES = [
    {
      name: 'Gmail',
      // Search instead of the inbox, so the confirmation is found in spam too
      url: 'https://mail.google.com/mail/u/0/#search/from%3Amara-shop.com+in%3Aanywhere',
      domains: ['gmail.com', 'googlemail.com'],
    },
    {
      name: 'Outlook',
      url: 'https://outlook.live.com/mail/',
      domains: ['outlook.com', 'outlook.de', 'hotmail.com', 'hotmail.de', 'live.com', 'live.de', 'msn.com'],
    },
    { name: 'GMX', url: 'https://www.gmx.net/', domains: ['gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'gmx.com'] },
    { name: 'WEB.DE', url: 'https://web.de/', domains: ['web.de'] },
    { name: 'T-Online', url: 'https://email.t-online.de/', domains: ['t-online.de', 'magenta.de'] },
    { name: 'iCloud Mail', url: 'https://www.icloud.com/mail/', domains: ['icloud.com', 'me.com', 'mac.com'] },
    { name: 'Yahoo Mail', url: 'https://mail.yahoo.com/', domains: ['yahoo.com', 'yahoo.de', 'ymail.com'] },
    { name: 'Posteo', url: 'https://posteo.de/webmail/', domains: ['posteo.de', 'posteo.net'] },
    { name: 'freenet Mail', url: 'https://webmail.freenet.de/', domains: ['freenet.de'] },
    { name: 'o2 Mail', url: 'https://email.o2online.de/', domains: ['o2mail.de', 'o2online.de'] },
    { name: 'Proton Mail', url: 'https://mail.proton.me/', domains: ['proton.me', 'protonmail.com', 'pm.me'] },
  ];

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Shopify's Customer Privacy API; null if it does not load (the popup then does not wait for it)
  const privacyApi = Promise.race([
    new Promise((resolve) => {
      try {
        if (window.Shopify.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) =>
          resolve(error ? null : window.Shopify.customerPrivacy || null)
        );
      } catch {
        resolve(null);
      }
    }),
    wait(PRIVACY_API_TIMEOUT).then(() => null),
  ]);

  // Resolves once no cookie banner is open: right away if the visitor decided before or sees no
  // banner, otherwise BANNER_GAP after the decision. shouldShowBanner() only reflects region and
  // shop settings, so an earlier decision is read from currentVisitorConsent() ('' = undecided).
  const bannerDone = privacyApi.then((api) => {
    try {
      if (!api || !api.shouldShowBanner()) return;
      const consent = api.currentVisitorConsent();
      if (['analytics', 'marketing', 'preferences'].some((key) => consent[key])) return;
    } catch {
      return;
    }
    return new Promise((resolve) => {
      document.addEventListener('visitorConsentCollected', () => setTimeout(resolve, BANNER_GAP), { once: true });
    });
  });

  const preferencesAllowed = () => {
    try {
      return Boolean(window.Shopify.customerPrivacy.preferencesProcessingAllowed());
    } catch {
      return false;
    }
  };

  // Blocked site data throws on every access, including the storage getter itself
  const readJSON = (storageName, key) => {
    try {
      return JSON.parse(window[storageName].getItem(key)) || {};
    } catch {
      return {};
    }
  };

  const writeJSON = (storageName, key, value) => {
    try {
      window[storageName].setItem(key, JSON.stringify(value));
    } catch {
      // No storage: the popup simply shows again on the next page
    }
  };

  const readState = () => ({ ...readJSON('localStorage', STORAGE_KEY), ...readJSON('sessionStorage', STORAGE_KEY) });

  const writeState = (patch) => {
    const storageName = preferencesAllowed() ? 'localStorage' : 'sessionStorage';
    writeJSON(storageName, STORAGE_KEY, { ...readJSON(storageName, STORAGE_KEY), ...patch });
  };

  class SignupPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      // Own error message instead of the browser bubble
      this.form.noValidate = true;
      this.emailInput = this.querySelector('input[type="email"]');
      this.errorEl = this.querySelector('[data-signup-popup-error]');
      this.submitButton = this.querySelector('[data-signup-popup-submit]');
      this.formId = this.dataset.formId || 'signup-popup';
      this.device = window.matchMedia('(hover: hover) and (pointer: fine)').matches ? 'desktop' : 'mobile';
      this.messages = Object.fromEntries(
        [...this.querySelector('[data-signup-popup-messages]').content.querySelectorAll('[data-key]')].map((el) => [
          el.dataset.key,
          el.textContent.trim(),
        ])
      );

      this.bindDialog();
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.querySelector('[data-signup-popup-retry]').addEventListener('click', () => this.showView('form', true));

      if (this.dataset.designMode !== undefined) {
        document.addEventListener('shopify:section:select', (event) => {
          if (this.closest(`#shopify-section-${event.detail.sectionId}`)) this.open({ force: true, track: false });
        });
        document.addEventListener('shopify:section:deselect', () => this.dialog.open && this.dialog.close());
        return;
      }

      // Return from the native post: the page was rendered with the result. Only this form's
      // redirect carries the #SignupPopupForm hash.
      const params = new URLSearchParams(location.search);
      const posted = params.get('customer_posted') === 'true' && location.hash === `#${this.form.id}`;
      if (posted || this.errorEl.textContent.trim()) {
        const pending = readJSON('sessionStorage', PENDING_KEY);
        this.trigger = pending.trigger;
        if (posted) {
          this.onSuccess(pending.email || '');
          this.cleanUrl();
        }
        this.open({ force: true, track: false });
        return;
      }

      if (params.has('signup_popup')) {
        setTimeout(() => this.open({ force: true, track: false }), 1000);
        return;
      }

      const state = readState();
      if (state.subscribed) return;
      if (state.closedAt && Date.now() - state.closedAt < Number(this.dataset.pauseDays) * DAY) return;

      this.schedule();
    }

    schedule() {
      const seconds = (name) => Number(this.dataset[name]) * 1000;

      // Visible time adds up across the page views of the session, so browsing several pages
      // counts as well as staying on one
      const session = readJSON('sessionStorage', SESSION_KEY);
      const views = (session.views || 0) + 1;
      const sessionStart = session.ms || 0;
      let counted = sessionStart;
      let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
      const elapsed = () => counted + (visibleSince ? Date.now() - visibleSince : 0);
      const save = () => writeJSON('sessionStorage', SESSION_KEY, { ms: elapsed(), views });
      const onVisibility = () => {
        if (document.visibilityState === 'visible') {
          visibleSince = visibleSince || Date.now();
        } else {
          counted = elapsed();
          visibleSince = null;
          save();
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', save);
      save();

      let armed = false;
      const onMouseOut = (event) => {
        if (armed && !event.relatedTarget && event.clientY <= 0) this.open({ trigger: 'exit' });
      };
      // mouseleave catches fast exits that skip mouseout on the last element
      const onMouseLeave = (event) => {
        if (armed && event.clientY <= 0) this.open({ trigger: 'exit' });
      };
      if (this.device === 'desktop') {
        document.addEventListener('mouseout', onMouseOut);
        document.documentElement.addEventListener('mouseleave', onMouseLeave);
      }

      const tick = setInterval(() => {
        const total = elapsed();
        if (this.device === 'mobile') {
          if (total >= seconds('mobileDelay')) this.open({ trigger: 'delay' });
          else if (views >= 2 && total - sessionStart >= SECOND_VIEW_DELAY) this.open({ trigger: 'pageview' });
          return;
        }
        armed = total >= seconds('desktopMinDelay');
        if (total >= Math.max(seconds('desktopFallback'), seconds('desktopMinDelay'))) {
          this.open({ trigger: 'exit_fallback' });
        }
      }, 1000);

      this.cleanup = () => {
        clearInterval(tick);
        save();
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', save);
        document.removeEventListener('mouseout', onMouseOut);
        document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      };
    }

    async open({ trigger, force = false, track = true } = {}) {
      if (this.dialog.open || this.opening) return;
      this.opening = true;
      this.cleanup?.();

      if (!force) {
        await bannerDone;
        // Wait while a drawer, menu or other dialog is open
        while (document.querySelector('dialog[open]') || document.body.classList.contains('overflow-hidden')) {
          await wait(2000);
        }
      }

      this.opening = false;
      if (trigger) this.trigger = trigger;
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      this.querySelector('[data-signup-popup-view]:not([hidden]) h2').focus();
      if (track) this.publish('popup_view', {});
    }

    bindDialog() {
      this.querySelector('[data-signup-popup-close]').addEventListener('click', () => this.close('button'));
      // Esc
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close('escape');
      });
      // Clicks on the backdrop land on the dialog element itself
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close('backdrop');
      });
      // Keep Tab inside the dialog (showModal alone lets focus move to the browser UI)
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [
          ...this.dialog.querySelectorAll('button, [href], input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
        ].filter((el) => !el.closest('[hidden]') && !el.hidden && el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      this.dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
    }

    close(method) {
      if (!this.dialog.open) return;
      this.dialog.close();
      if (this.dataset.designMode !== undefined) return;
      if (!this.subscribed) writeState({ closedAt: Date.now() });
      this.publish('popup_close', { method, subscribed: Boolean(this.subscribed) });
    }

    // Added by Shopify's hCaptcha script once a field of the form got focus
    captchaField() {
      return this.form.querySelector('[name="h-captcha-response"]');
    }

    onSubmit(event) {
      const email = this.emailInput.value.trim();
      if (!this.emailInput.checkValidity()) {
        event.preventDefault();
        this.showError(this.messages.invalid);
        return;
      }
      writeJSON('sessionStorage', PENDING_KEY, { email, trigger: this.trigger });
      if (!this.busy) this.publish('popup_submit', {});

      // Shopify's hCaptcha script takes over: it gets a token and posts the form natively
      if (this.captchaField()) {
        this.setBusy(true);
        // Reset if the post never happens, e.g. a dismissed hCaptcha challenge
        clearTimeout(this.busyTimer);
        this.busyTimer = setTimeout(() => this.setBusy(false), 15000);
        return;
      }

      event.preventDefault();
      if (this.busy) return;
      this.setBusy(true);
      this.submitWhenReady(email);
    }

    // Submitted before hCaptcha was loaded (e.g. autofill and Enter right away): a post without
    // token is rejected (400 "Missing CAPTCHA token"), so wait for the script and submit again
    async submitWhenReady(email) {
      this.emailInput.focus();
      for (let waited = 0; waited < CAPTCHA_WAIT && !this.captchaField(); waited += 200) {
        await wait(200);
      }
      if (this.captchaField()) {
        this.form.requestSubmit();
        return;
      }

      // No hCaptcha (spam protection switched off): post in the background
      try {
        const response = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
        if (new URL(response.url).searchParams.get('customer_posted') === 'true') {
          this.onSuccess(email);
          return;
        }
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const message = html.querySelector('[data-signup-popup-error]')?.textContent.trim();
        this.showError(message || this.messages.error);
      } catch {
        this.showError(this.messages.error);
      } finally {
        this.setBusy(false);
      }
    }

    onSuccess(email) {
      this.subscribed = true;
      writeState({ subscribed: true });
      this.publish('popup_signup', {});

      this.querySelector('[data-signup-popup-email]').textContent = email;
      const domain = email.split('@')[1]?.toLowerCase();
      const mailbox = MAILBOXES.find((entry) => entry.domains.includes(domain));
      const link = this.querySelector('[data-signup-popup-mailbox]');
      link.hidden = !mailbox;
      if (mailbox) {
        link.href = mailbox.url;
        link.textContent = link.dataset.label.replace('[provider]', mailbox.name);
      }
      this.showView('success');
    }

    // Drop the post result from the address, so a reload or shared link does not show it again
    cleanUrl() {
      try {
        const url = new URL(location.href);
        url.searchParams.delete('customer_posted');
        url.hash = '';
        history.replaceState(history.state, '', url);
      } catch {}
    }

    showView(name, focusInput = false) {
      this.querySelectorAll('[data-signup-popup-view]').forEach((view) => {
        view.hidden = view.dataset.signupPopupView !== name;
      });
      if (name === 'form') {
        this.hideError();
        this.setBusy(false);
        if (focusInput) {
          this.emailInput.value = '';
          this.emailInput.focus();
        }
      } else {
        this.querySelector('[data-signup-popup-success-title]').focus();
      }
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
    }

    hideError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.emailInput.removeAttribute('aria-invalid');
    }

    // No `disabled`: Shopify's hCaptcha script submits the form after this is set
    setBusy(busy) {
      this.busy = busy;
      this.submitButton.setAttribute('aria-disabled', String(busy));
      this.submitButton.setAttribute('aria-busy', String(busy));
      if (busy) {
        if (!this.submitButton.dataset.label) this.submitButton.dataset.label = this.submitButton.textContent;
        this.submitButton.textContent = this.messages.sending;
      } else if (this.submitButton.dataset.label) {
        this.submitButton.textContent = this.submitButton.dataset.label;
        delete this.submitButton.dataset.label;
      }
    }

    // GA4 events through the Google & YouTube app's gtag, which already follows the cookie consent.
    // A custom pixel would run gtag in Shopify's sandbox, which sent scroll/session_start/first_visit
    // from the sandbox URL and broke session attribution. This relies on the app exposing
    // window.gtag on the page; the app creates it at runtime, so popup_signup (sent right after the
    // reload) waits for it instead of queueing into dataLayer ahead of the app's consent and config.
    // Sent only with analytics consent, whatever consent mode the app uses (privacy policy: no GA4
    // without consent).
    publish(name, data) {
      const params = {
        send_to: GA4_ID,
        form_id: this.formId,
        device: this.device,
        popup_trigger: this.trigger,
        close_method: data.method,
      };
      const started = Date.now();
      const send = () => {
        const privacy = window.Shopify?.customerPrivacy;
        if (typeof window.gtag === 'function' && privacy) {
          try {
            if (privacy.analyticsProcessingAllowed()) window.gtag('event', name, params);
          } catch {}
        } else if (Date.now() - started < GTAG_TIMEOUT) {
          setTimeout(send, GTAG_POLL);
        }
      };
      send();
    }
  }

  customElements.define('signup-popup', SignupPopup);
}
